import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./NotificationDialog.css";

export const NotificationDialog = ({
  open,
  onClose,
  notifications,
  onDismiss,
}) => {
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) {
      setActiveIndex(0);
      setDoNotShowAgain(false);
    }
  }, [open]);

  const handleClose = () => {
    if (doNotShowAgain && notifications.length > 0) {
      const notificationIds = notifications.map((notif) => notif._id);
      onDismiss(notificationIds);
    }
    onClose();
  };

  const renderBugNotification = (notification) => {
    let bugData = null;
    try {
      const jsonStr = notification.body.replace("[BUG_JSON]:", "");
      bugData = JSON.parse(jsonStr);
    } catch (e) {
      console.error("Failed to parse bug notification JSON:", e);
      return <Typography color="error">Failed to load bug squash details.</Typography>;
    }

    const resolveImgSrc = (url) => {
      const raw = String(url || "").trim();
      if (!raw) return "";
      if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
      if (raw.startsWith("/assets/")) return raw;
      if (raw.startsWith("/uploads/")) return `${process.env.REACT_APP_BACKEND_URL}${raw}`;
      if (raw.startsWith("/")) return raw;
      return raw;
    };

    const awardImgUrl = resolveImgSrc(bugData.awardItemImg);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          gap: 3,
          p: 2.5,
          borderRadius: "16px",
          background: "linear-gradient(135deg, #fdf5e6 0%, #faebd7 100%)",
          border: "2px solid #a52a2a",
          boxShadow: "0 6px 18px rgba(165,42,42,0.15)",
        }}
      >
        {/* Left Side: CSS bug badge */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: { xs: 96, sm: 112 },
            height: { xs: 96, sm: 112 },
            flexShrink: 0,
          }}
        >
          <Box className="bug-squashed-css-badge" aria-label="Bug Squashed" />
        </Box>

        {/* Right Side: Details */}
        <Box sx={{ flexGrow: 1, textAlign: "left", width: "100%" }}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: "'Pixelify Sans', sans-serif !important",
              fontWeight: "bold",
              color: "#a52a2a",
              mb: 0.5,
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            Bug Squashed!
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: "'Pixelify Sans', sans-serif !important",
              fontWeight: 700,
              color: "#5c3e21",
              mb: 1.5,
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            "{bugData.bugTitle}"
          </Typography>

          {/* Details Card */}
          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.65)",
              border: "1px solid rgba(165, 42, 42, 0.15)",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography variant="body2">
              <strong>Category:</strong> <span style={{ color: "#a52a2a", fontWeight: "bold" }}>{bugData.bugType}</span>
            </Typography>

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <strong>Description:</strong> {bugData.description}
            </Typography>

            <Divider sx={{ my: 0.5 }} />

            <Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: "bold" }}>
              Reward Points: +{bugData.pointsAwarded} Bug Points
            </Typography>

            {bugData.rankChange && (
              <Typography variant="body2" sx={{ color: "#1976d2", fontWeight: "bold" }}>
                Leaderboard Standings: {bugData.rankChange}
              </Typography>
            )}

            <Divider sx={{ my: 0.5 }} />

            {/* Award Item */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              {bugData.awardItemImg && (
                <img
                  src={awardImgUrl}
                  alt={bugData.awardItemName}
                  style={{ width: 32, height: 32, objectFit: "contain" }}
                />
              )}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: "bold", color: "#715024" }}>
                  Loot Won: {bugData.awardItemQty}x {bugData.awardItemName}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                  (Credited directly to your inventory)
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
    );
  };

  const totalNotifications = notifications.length;
  const pageLabel = `${Math.min(activeIndex + 1, Math.max(totalNotifications, 1))}/${Math.max(
    totalNotifications,
    1
  )}`;
  const currentNotification = notifications[activeIndex] || null;
  const readingMinutes = useMemo(() => {
    const bodyText = String(currentNotification?.body || "");
    const words = bodyText.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 180));
  }, [currentNotification?.body]);

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    centerMode: true,
    centerPadding: "0px",
    afterChange: (nextIndex) => setActiveIndex(nextIndex),
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        className: "daily-news-scroll-dialog guild-collect-dialog-paper",
      }}
    >
      <DialogTitle className="daily-news-scroll-title guild-collect-dialog-title guild-collect-dialog-title-craft">
        Hoarding Life Daily News
      </DialogTitle>
      <DialogContent className="daily-news-scroll-content guild-collect-dialog-content">
        <Box className="guild-collect-scroll-shell">
          <Typography className="guild-collect-kicker">
            Hoarding Life Guild Bulletin
          </Typography>
          <Box className="guild-collect-chip-row daily-news-scroll-chip-row">
            <span className="guild-collect-chip guild-collect-chip-primary">
              {`Dispatch ${pageLabel}`}
            </span>
            <span className="guild-collect-chip">{`${readingMinutes} min read`}</span>
            <span className="guild-collect-chip">{`${totalNotifications} active scroll${
              totalNotifications === 1 ? "" : "s"
            }`}</span>
          </Box>
        </Box>

        {notifications.length > 0 ? (
          <Box className="daily-news-scroll-slider-wrap">
            <Slider {...settings}>
              {notifications.map((notification, index) => {
                const isBugNotification = String(notification?.body || "").startsWith("[BUG_JSON]:");
                if (isBugNotification) {
                  return (
                    <Box key={notification._id || `daily-news-${index}`} className="daily-news-entry" sx={{ p: 1 }}>
                      {renderBugNotification(notification)}
                    </Box>
                  );
                }

                const paragraphs = String(notification?.body || "")
                  .split(/\n{2,}/)
                  .map((paragraph) => paragraph.trim())
                  .filter(Boolean);

                return (
                  <Box key={notification._id || `daily-news-${index}`} className="daily-news-entry">
                    <Typography className="daily-news-entry-title">
                      {notification?.title || "Guild Update"}
                    </Typography>

                    {notification?.img ? (
                      <Box className="daily-news-entry-image-wrap">
                        <img
                          src={notification.img}
                          alt={notification?.title || "Guild update"}
                          className="daily-news-entry-image"
                          loading="lazy"
                        />
                      </Box>
                    ) : null}

                    <Stack spacing={1.2} className="daily-news-entry-body">
                      {(paragraphs.length > 0 ? paragraphs : ["No details provided."]).map(
                        (paragraph, paragraphIndex) => (
                          <Typography key={`${notification?._id || index}-p-${paragraphIndex}`}>
                            {paragraph}
                          </Typography>
                        )
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Slider>
          </Box>
        ) : (
          <Typography className="daily-news-empty">No notifications to display.</Typography>
        )}

        <Box className="daily-news-scroll-footer">
          <FormControlLabel
            control={
              <Checkbox
                checked={doNotShowAgain}
                onChange={(e) => setDoNotShowAgain(e.target.checked)}
              />
            }
            label="Seal all current scrolls (do not show again)"
            className="daily-news-dismiss-label"
          />
          <Button onClick={handleClose} className="daily-news-close-btn">
            Seal Scroll
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
